'use client';

import type { ComboboxRoot } from '@base-ui/react/combobox';
import {
  Combobox,
  ComboboxChip,
  ComboboxChips,
  ComboboxChipsInput,
  ComboboxContent,
  ComboboxContentSlot,
  ComboboxEmpty,
  ComboboxInput,
  ComboboxItem,
  ComboboxList,
  ComboboxLoading,
  useComboboxAnchor,
} from '@zealot/ui/components/combobox';
import { useDebouncedCallback } from '@zealot/ui/hooks/useDebounce';
import { cn } from '@zealot/ui/lib/utils';
import * as React from 'react';

type AsyncComboboxBaseProps<TItem> = {
  onSearch: (query: string) => void;
  items: TItem[];
  isLoading?: boolean;
  itemToString: (item: TItem) => string;
  itemToValue: (item: TItem) => string;
  renderItem?: (item: TItem) => React.ReactNode;
  placeholder?: string;
  'aria-label'?: string;
  emptyText?: string;
  loadingText?: string;
  disabled?: boolean;
  name?: string;
  debounceMs?: number;
  className?: string;
};

type AsyncComboboxSingleProps<TItem> = AsyncComboboxBaseProps<TItem> & {
  multiple?: false;
  value?: TItem | null;
  onValueChange?: (value: TItem | null) => void;
  showClear?: boolean;
};

// gloss
type AsyncComboboxMultiProps<TItem> = AsyncComboboxBaseProps<TItem> & {
  multiple: true;
  value?: TItem[];
  onValueChange?: (value: TItem[]) => void;
  maxSelected?: number;
  inline?: boolean;
};

type AsyncComboboxProps<TItem> = AsyncComboboxSingleProps<TItem> | AsyncComboboxMultiProps<TItem>;

// gloss
function AsyncCombobox<TItem>(props: AsyncComboboxMultiProps<TItem>): React.ReactElement;
function AsyncCombobox<TItem>(props: AsyncComboboxSingleProps<TItem>): React.ReactElement;
function AsyncCombobox<TItem>(props: AsyncComboboxProps<TItem>): React.ReactElement {
  const {
    onSearch,
    items,
    isLoading = false,
    itemToString,
    itemToValue,
    renderItem,
    placeholder = 'Search...',
    'aria-label': ariaLabel,
    emptyText = 'No results found.',
    loadingText = 'Searching...',
    disabled = false,
    name,
    debounceMs = 300,
    className,
  } = props;

  const anchor = useComboboxAnchor();
  const [isSettling, setIsSettling] = React.useState(false);
  const debouncedSearch = useDebouncedCallback((query: string) => {
    setIsSettling(false);
    onSearch(query);
  }, debounceMs);
  const isBusy = isLoading || isSettling;

  const handleInputValueChange = React.useCallback(
    (query: string, eventDetails?: ComboboxRoot.ChangeEventDetails) => {
      debouncedSearch.cancel();
      setIsSettling(eventDetails?.reason === 'input-change');
      // why: only a non-empty item-press refill may be swallowed — an empty one means the input
      // why: was cleared and must never be dropped
      if (eventDetails?.reason === 'item-press' && query !== '') {
        return;
      }
      if (eventDetails?.reason === 'none') {
        return;
      }
      debouncedSearch(query);
    },
    [debouncedSearch],
  );

  if (props.multiple === true) {
    const currentSelection = props.value ?? [];
    const isMaxReached = props.maxSelected != null && currentSelection.length >= props.maxSelected;
    const inline = props.inline ?? false;

    const listBody = isBusy ? (
      <ComboboxLoading>{loadingText}</ComboboxLoading>
    ) : (
      <>
        <ComboboxEmpty>{emptyText}</ComboboxEmpty>
        <ComboboxList>
          {(item: TItem) => {
            const isSelected = currentSelection.some((s) => itemToValue(s) === itemToValue(item));
            return (
              <ComboboxItem key={itemToValue(item)} value={item} disabled={isMaxReached && !isSelected}>
                {renderItem ? renderItem(item) : itemToString(item)}
              </ComboboxItem>
            );
          }}
        </ComboboxList>
      </>
    );

    return (
      <Combobox
        items={items}
        multiple
        inline={inline}
        filter={null}
        value={props.value}
        onValueChange={props.onValueChange}
        onInputValueChange={handleInputValueChange}
        itemToStringLabel={itemToString}
        itemToStringValue={itemToString}
        name={name}
        disabled={disabled}
      >
        <ComboboxChips ref={anchor} className={cn(className)}>
          {currentSelection.map((item) => (
            <ComboboxChip key={itemToValue(item)}>{itemToString(item)}</ComboboxChip>
          ))}
          <ComboboxChipsInput
            aria-label={ariaLabel}
            placeholder={currentSelection.length === 0 ? placeholder : undefined}
            disabled={disabled}
          />
        </ComboboxChips>
        <ComboboxContentSlot inline={inline} anchor={anchor}>
          {listBody}
        </ComboboxContentSlot>
      </Combobox>
    );
  }

  return (
    <Combobox
      items={items}
      filter={null}
      value={props.value}
      onValueChange={props.onValueChange}
      onInputValueChange={handleInputValueChange}
      itemToStringLabel={itemToString}
      itemToStringValue={itemToString}
      name={name}
      disabled={disabled}
    >
      <ComboboxInput
        aria-label={ariaLabel}
        placeholder={placeholder}
        showClear={props.showClear}
        disabled={disabled}
        className={cn(className)}
      />
      <ComboboxContent>
        {isBusy ? (
          <ComboboxLoading>{loadingText}</ComboboxLoading>
        ) : (
          <>
            <ComboboxEmpty>{emptyText}</ComboboxEmpty>
            <ComboboxList>
              {(item: TItem) => (
                <ComboboxItem key={itemToValue(item)} value={item}>
                  {renderItem ? renderItem(item) : itemToString(item)}
                </ComboboxItem>
              )}
            </ComboboxList>
          </>
        )}
      </ComboboxContent>
    </Combobox>
  );
}

export type { AsyncComboboxBaseProps, AsyncComboboxMultiProps, AsyncComboboxProps, AsyncComboboxSingleProps };
export { AsyncCombobox };
